import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs, where, doc, getDoc } from '../firebaseConfig';
import { Line } from 'react-chartjs-2';
import Card from '@mui/material/Card';
import MDBox from '../components/MDBox';
import Divider from '@mui/material/Divider';
import AssessmentIcon from '@mui/icons-material/Assessment';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { useTheme } from '@mui/material/styles';
import MDTypography from '../components/MDTypography';
import { CircularProgress } from '@mui/material';
import { usePeriod } from '../components/PeriodContext';

const TeamRiskOverTimeChart = ({ userID, tradeAccountID = 'default' }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [trades, setTrades] = useState([]);
  const [capital, setCapital] = useState(0);
  const [accountName, setAccountName] = useState(tradeAccountID);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  // Fetch account name
  useEffect(() => {
    const fetchAccountName = async () => {
      if (tradeAccountID === 'default') {
        setAccountName('Default');
        return;
      }
      try {
        const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
        if (accountDoc.exists()) {
          setAccountName(accountDoc.data().name || tradeAccountID);
        }
      } catch (error) {
        console.error('Error fetching account name:', error);
      }
    };
    fetchAccountName();
  }, [userID, tradeAccountID]);

  // Fetch trades and capital
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch capital data
        const capitalPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/capital`;
        const capitalQuery = query(collection(db, capitalPath));
        const capitalSnapshot = await getDocs(capitalQuery);
        let totalDeposits = 0;
        let totalWithdrawals = 0;

        capitalSnapshot.forEach((doc) => {
          const data = doc.data();
          totalDeposits += data.deposit || 0;
          totalWithdrawals += data.withdrawal || 0;
        });

        // Fetch trades
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(collection(db, tradesPath));
        const tradesSnapshot = await getDocs(tradesQuery);
        let totalTradeReturns = 0;
        const tradesData = tradesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date instanceof Object ? doc.data().date.toDate() : new Date(doc.data().date),
        }));

        tradesData.forEach((trade) => {
          totalTradeReturns += trade.returns || 0;
        });

        const updatedCapital = totalDeposits - totalWithdrawals + totalTradeReturns;
        setCapital(updatedCapital);
        setTrades(tradesData);
        setHasData(tradesData.length > 0);
        console.log('Fetched trades for userID:', userID, 'tradeAccountID:', tradeAccountID, 'Count:', tradesData.length);
      } catch (error) {
        console.error('Error fetching data for TeamRiskOverTimeChart:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userID, tradeAccountID]);

  const filterEntries = (entries) => {
    const matchesPeriod = (entryDate) => {
      const date = new Date(entryDate);
      if (selectedPeriod === 'today') {
        return date.toDateString() === new Date().toDateString();
      } else if (selectedPeriod === 'this-week') {
        const getWeek = (d) => {
          const date = new Date(d.getTime());
          date.setHours(0, 0, 0, 0);
          date.setDate(date.getDate() + 4 - (date.getDay() || 7));
          const yearStart = new Date(date.getFullYear(), 0, 1);
          return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
        };
        return getWeek(date) === getWeek(new Date()) && date.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'this-month') {
        return date.getMonth() === new Date().getMonth() && date.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'this-year') {
        return date.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'pick-month') {
        return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
      } else if (selectedPeriod === 'pick-year') {
        return date.getFullYear() === selectedYear;
      } else if (selectedPeriod === 'pick-month-year') {
        return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
      } else {
        return true; // all-time
      }
    };

    return entries.filter((entry) => {
      const matchesTag = selectedTag === 'all' || (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));
      return matchesPeriod(entry.date) && entry.status === (showMissedTrades ? 'Missed' : 'Closed') && matchesTag;
    });
  };

  const calculateMonthlyAvgRisk = useMemo(() => {
    const filteredTrades = filterEntries(trades);
    const avgRiskByMonth = {};

    filteredTrades.forEach((trade) => {
      const riskAmount = parseFloat(trade.riskAmount) || 0;
      const tradeDate = new Date(trade.date);
      const month = tradeDate.getMonth() + 1;

      if (!avgRiskByMonth[month]) {
        avgRiskByMonth[month] = { totalRisk: 0, numTrades: 0 };
      }
      if (!isNaN(riskAmount) && capital > 0) {
        avgRiskByMonth[month].totalRisk += riskAmount;
        avgRiskByMonth[month].numTrades++;
      }
    });

    for (const month in avgRiskByMonth) {
      if (avgRiskByMonth[month].numTrades > 0) {
        const avgRiskAmount = avgRiskByMonth[month].totalRisk / avgRiskByMonth[month].numTrades;
        avgRiskByMonth[month] = (avgRiskAmount / capital) * 100;
      } else {
        avgRiskByMonth[month] = 0;
      }
    }

    console.log('Calculated monthly average risk:', avgRiskByMonth);
    return avgRiskByMonth;
  }, [trades, capital, selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag]);

  const data = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Monthly Average Risk Per Trade (%)',
        data: Array.from({ length: 12 }, (_, i) => calculateMonthlyAvgRisk[i + 1] || 0),
        fill: false,
        backgroundColor: 'rgb(255, 99, 132)',
        borderColor: 'rgba(255, 99, 132, 0.2)',
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (value) => `${value.toFixed(2)}%` },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => `Avg Risk: ${context.parsed.y.toFixed(2)}%`,
        },
      },
    },
  };

  const tooltipContent = `The Risk Over Time Analysis tracks your average risk per trade as a percentage of capital for ${accountName}, displaying monthly risk trends. Filter by period and tags to analyze specific strategies. Higher percentages indicate larger positions, helping identify risk management patterns. Note: Risk should typically be 1-2% of capital per trade.`;

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </MDBox>
      );
    }
    if (!hasData) {
      return (
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
          textAlign="center"
          px={2}
        >
          <InfoIcon sx={{ fontSize: 40, mb: 2, color: iconColor }} />
          <MDTypography variant="h6" color="text">
            No trading data available
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Complete some trades to see your risk analysis
          </MDTypography>
        </MDBox>
      );
    }
    return (
      <MDBox height="100%">
        <Line data={data} options={chartOptions} />
      </MDBox>
    );
  };

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <AssessmentIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Monthly Average Risk {tradeAccountID === 'default' ? '' : `(${accountName})`} {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  },
                },
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        {renderContent()}
      </MDBox>
    </Card>
  );
};

TeamRiskOverTimeChart.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

export default TeamRiskOverTimeChart;